export type TriggerEnvironmentType = 'PRODUCTION' | 'STAGING' | 'DEVELOPMENT' | 'PREVIEW';

type EnvironmentValues = Record<string, string | undefined>;

type ServerTelemetryOptions = {
  env?: EnvironmentValues;
  triggerEnvironmentType?: TriggerEnvironmentType;
};

type BrowserTelemetryOptions = {
  env?: EnvironmentValues;
  hostname?: string;
};

/**
 * Vercel exposes the platform marker, environment and target at build and
 * runtime. Checking all three prevents a custom target such as `staging` from
 * being treated as production even if it inherits production settings.
 */
export function isVercelProductionEnvironment(env: EnvironmentValues = process.env): boolean {
  if (env.VERCEL !== '1') return false;
  if (env.VERCEL_ENV !== 'production') return false;
  return !env.VERCEL_TARGET_ENV || env.VERCEL_TARGET_ENV === 'production';
}

/** Trigger.dev's documented run context uses these uppercase environment types. */
export function isTriggerProductionEnvironment(
  environmentType: string | undefined,
): environmentType is 'PRODUCTION' {
  return environmentType === 'PRODUCTION';
}

/**
 * Server telemetry fails closed unless it is running in Vercel production or
 * a Trigger.dev task explicitly supplies a PRODUCTION context.
 */
export function isServerTelemetryEnabled(options: ServerTelemetryOptions = {}): boolean {
  if (options.triggerEnvironmentType !== undefined) {
    return isTriggerProductionEnvironment(options.triggerEnvironmentType);
  }

  const env = options.env ?? process.env;
  // Rendering still needs to know it is a production deployment, but exporters
  // must not start in the short-lived Next.js build process.
  if (env.NEXT_PHASE === 'phase-production-build') return false;
  return isVercelProductionEnvironment(env);
}

export function isLocalhostHostname(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '::1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.localhost') ||
    hostname.endsWith('.local')
  );
}

/**
 * Browser code receives Vercel's deployment values through next.config.js.
 * The origin check is a second guard for locally served production bundles.
 */
export function isBrowserTelemetryEnabled(options: BrowserTelemetryOptions = {}): boolean {
  // Keep the default references static so Next.js can inline NEXT_PUBLIC values
  // into the browser bundle. Dynamic process.env lookups are not replaced.
  const env = options.env ?? {
    NEXT_PUBLIC_VERCEL: process.env.NEXT_PUBLIC_VERCEL,
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_VERCEL_TARGET_ENV: process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV,
  };
  const hostname =
    options.hostname ?? (typeof window === 'undefined' ? undefined : window.location.hostname);

  if (hostname && isLocalhostHostname(hostname)) return false;
  if (env.NEXT_PUBLIC_VERCEL !== '1') return false;
  if (env.NEXT_PUBLIC_VERCEL_ENV !== 'production') return false;
  return !env.NEXT_PUBLIC_VERCEL_TARGET_ENV || env.NEXT_PUBLIC_VERCEL_TARGET_ENV === 'production';
}

export function isBrowserTelemetryDisabled(): boolean {
  return !isBrowserTelemetryEnabled();
}
